import { useOutletContext } from "react-router";
import { orderedCategories } from "@medoma/models/activities";
import {
  type IMedicalCompetence,
  orderedCompetences,
} from "@medoma/models/shifts";
import type { IActivityTemplate, IBatchTemplate } from "@/api/Templates";
import type { TemplateFormFields } from "./TemplateForm/templateFormSchema";
import { patientTypesSchema, type IPatientType } from "@medoma/models/patients";

// The fields both template form variants share, as one flat object rather than a
// union. A batch template's activities satisfy the same shape without being a
// template form themselves, which is why the bound below is the shared fields and
// not TemplateFormFields.
type ISharedTemplateFormFields = Pick<
  TemplateFormFields,
  keyof TemplateFormFields
>;

/**
 * Utility function that maps "empty" checkbox fields from boolean (false) to empty array ([])
 * React hook form sets ´false´ to registered checkbox inputs when no checkbox is checked,
 * but we think our model makes more sense when these fields are empty arrays.
 */
export const mapEmptyCheckboxFieldsFromFalseToEmptyArray = <
  T extends ISharedTemplateFormFields,
>(
  formData: T,
): T => {
  const result: Record<string, unknown> = { ...formData };

  if ("measurements" in formData && result.measurements === false) {
    result.measurements = [];
  }

  if ("weekdays" in formData && result.weekdays === false) {
    result.weekdays = [];
  }

  if (result.requiredCompetences === false) {
    result.requiredCompetences = [];
  }

  return result as T;
};

export const useParentRoutePatientTypes = () => {
  return patientTypesSchema.parse(
    useOutletContext<{ patientTypes: IPatientType[] }>().patientTypes,
  );
};

const getTemplateCompetencePriority = (
  competences: IMedicalCompetence[] | undefined | null,
): number => {
  if (!competences || competences.length === 0) {
    return 999;
  }

  const prioritiesFromOrderedCompetences = competences
    .map((competence) => orderedCompetences.indexOf(competence))
    .filter((index) => index !== -1);

  if (prioritiesFromOrderedCompetences.length === 0) {
    return 999;
  }

  return Math.min(...prioritiesFromOrderedCompetences);
};

export const sortActivityTemplates = (templates: IActivityTemplate[]) => {
  return templates.sort((a, b) => {
    const aCategory = a.template.category;
    const bCategory = b.template.category;

    // First sort by category
    const aCategoryIndex = orderedCategories.indexOf(aCategory);
    const bCategoryIndex = orderedCategories.indexOf(bCategory);
    if (aCategoryIndex !== bCategoryIndex) {
      return aCategoryIndex - bCategoryIndex;
    }

    // Then sort by competence priority
    const aCompetencePriority = getTemplateCompetencePriority(
      a.template.requiredCompetences,
    );
    const bCompetencePriority = getTemplateCompetencePriority(
      b.template.requiredCompetences,
    );
    if (aCompetencePriority !== bCompetencePriority) {
      return aCompetencePriority - bCompetencePriority;
    }

    // Finally sort by title alphabetically
    return a.template.title.localeCompare(b.template.title);
  });
};

export const sortBatchTemplates = (templates: IBatchTemplate[]) => {
  return templates.sort((a, b) => {
    // Sort batch templates by title alphabetically
    return a.template.title.localeCompare(b.template.title);
  });
};
